import { useNavigate } from 'react-router-dom';
import { useTheme } from 'styled-components';
import { TripDetails } from '../../../API/types/Trips';
import Tags from '../../../components/common/Tags';
import { ListItemWrapper } from '../../../components/ui/ListItemWrapper/ListItemWrapper';
import EntityLink from '../../../components/ui/EntityLink/EntityLink';
import { getFormattedDateAndTime, getTimeZone, getTripDuration } from '../../../utils/dateUtils';
import { generateDistance, calculateAverageSpeed } from '../../../utils/unitsUtils';
import { IconKeys } from '../../../components/common/Icon/IconKeys';
import { ThemeType } from '../../../theme/themeType';
import useDriverThumbnail from '../../../theme/useDriverThumbnail';
import OngoingTripStatus from './OngoingTripStatus/OngoingTripStatus';
import { isTripOngoing } from '../../../utils/tripUtils';
import TripErrorsWarning from './TripErrorsWarning/TripErrorsWarning';
import useVehicleThumbnail from '../../../theme/useVehicleThumbnail';
import { ListNavigationState } from '../../../utils/listNavigation';

type Props = {
  item?: TripDetails;
  lightVersion?: boolean;
  filters?: Record<string, string[]>;
  listNavigation?: ListNavigationState;
};

function TripsItem({
  item, lightVersion, filters, listNavigation,
}: Props): JSX.Element {
  const theme = useTheme() as ThemeType;
  const vehicleThumbnail = useVehicleThumbnail();
  const driverThumbnail = useDriverThumbnail();
  const navigate = useNavigate();
  const oldestFirst = filters?.sortBy?.[0] === 'startAt_ASC';

  const {
    _id: id,
    vehicle,
    distanceInKm,
    startLocationName,
    endLocationName,
    startLocation,
    endLocation,
    startAt,
    endAt,
    alertCount,
    speedMs,
    driver,
    driverId,
    deviceId,
    timeSynchronization,
    startTimestampCorrupted,
    endTimestampCorrupted,
    lastActivityAtCorrupted,
    gpsCorrupted,
    isMergedTrip,
    mergedTripId,
  } = item!;

  const averageSpeed = calculateAverageSpeed(
    item?.distanceInKm ?? null,
    item?.durationInMs ?? (endAt && startAt ? new Date(endAt).getTime() - new Date(startAt).getTime() : null),
  );

  const detailTripId = (isMergedTrip && mergedTripId) ? mergedTripId : id;
  const vehicleDetailsId = vehicle?.vehicleId || vehicle?._id;
  const driverDetailsId = driverId || driver?.driverId || driver?._id;

  const tripErrors: string[] = [];
  if (timeSynchronization === 'NOT_SYNCHRONIZED' && endAt) tripErrors.push('Trip time might be incorrect');
  if (startTimestampCorrupted && endAt) tripErrors.push('Trip start time might be incorrect');
  if (endTimestampCorrupted && endAt) tripErrors.push('Trip end time might be incorrect');
  if (lastActivityAtCorrupted && endAt) tripErrors.push('Last activity time might be incorrect');
  if (gpsCorrupted && endAt) tripErrors.push('GPS location might be incorrect');

  return (
    <ListItemWrapper
      onClick={() => navigate(`/trips/${detailTripId}`, {
        state: { isMergedTrip: !!isMergedTrip, listNavigation },
      })}
      height="auto"
      tripItem
      data-list-item-id={id}
    >
      <Tags.Trip
        from={startLocationName || startLocation?.coordinates?.join(', ') || 'N/A'}
        to={endLocationName || endLocation?.coordinates?.join(', ') || 'N/A'}
      />
      {isTripOngoing(item!) && (
        <OngoingTripStatus
          startAt={startAt}
          speedMs={speedMs}
          deviceId={deviceId}
        />
      )}
      {isMergedTrip && (
        <Tags.Status
          label="Merged"
          backgroundColor={theme.colors.green}
          icon={IconKeys.MERGED_TRIP}
          width="fit-content"
        />
      )}
      <TripErrorsWarning errors={tripErrors} />
      {endAt && (
        <Tags.LabeledIcon
          label={getTripDuration(startAt, endAt)}
          icon={IconKeys.TIME}
        />
      )}
      {!!driver && (
        <EntityLink
          to={driverDetailsId ? `/drivers/${driverDetailsId}` : undefined}
          label={`Open driver ${driver.firstName} ${driver.lastName}`}
        >
          <Tags.Driver
            imageSrc={driver.avatarUrl || driverThumbnail}
            name={`${driver.firstName} ${driver.lastName}`}
            licenseName={driver.licenseName || ''}
          />
        </EntityLink>
      )}
      {!!vehicle && !lightVersion && (
        <EntityLink
          to={vehicleDetailsId ? `/vehicles/${vehicleDetailsId}` : undefined}
          label={`Open vehicle ${vehicle.licensePlateNumber}`}
        >
          <Tags.Vehicle
            imageSrc={vehicleThumbnail}
            licensePlateNumber={vehicle.licensePlateNumber}
            model={`${vehicle.manufacturer ? `${vehicle.manufacturer} ` : ''}${vehicle.model || ''}`}
          />
        </EntityLink>
      )}
      <Tags.StartEndTime
        startTime={`${getFormattedDateAndTime(startAt)} (${getTimeZone(startAt)})`}
        endTime={endAt ? `${getFormattedDateAndTime(endAt)} (${getTimeZone(endAt)})` : 'N/A'}
        oldestFirst={oldestFirst}
      />
      <Tags.LabeledIcon
        label={generateDistance(distanceInKm)}
        icon={IconKeys.DISTANCE}
      />
      {averageSpeed !== null && (
        <Tags.LabeledIcon
          label={`${averageSpeed.toFixed(1)} km/h`}
          icon={IconKeys.DISTANCE}
        />
      )}
      <Tags.LabeledIcon
        label={`${alertCount}`}
        icon={IconKeys.ALERT_CIRCLE}
        flexible
      />
      {/* {!lightVersion && <Tags.ProceedToDetails />} */}
    </ListItemWrapper>
  );
}

export default TripsItem;
